import { MailIdMarke } from "@/components/portal/MailIdMarke"
import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { ExternalLink, Paperclip, PencilLine } from "lucide-react"
import { api } from "@/lib/api"
import { ThreadTimeline } from "@/pages/emails/ThreadTimeline"
import { ModalShell } from "./ModalShell"
import { EmailBody } from "./EmailBody"
import { InhaltEntferntHinweis } from "./InhaltEntfernt"
import { AttachmentsSection } from "./AttachmentsSection"
import { StatusBadge } from "./StatusBadge"
import { HistoryContextBadge } from "./HistoryContextBadge"
import { fmtRelative } from "./LoadingError"
import { stripAttachmentDumps } from "@/lib/utils"
import type { EmailAntwort } from "@/lib/emailQuery"

/**
 * E-Mail als Popup — wie ein Doppelklick im Mailprogramm.
 *
 * Warum: Aus der Suche heraus ist die Mail ein Blick, kein Ortswechsel. Wer
 * waehrend eines Telefonats nachschaut, worum es in der letzten Mail ging,
 * will danach wieder in seiner Trefferliste stehen — nicht auf einer anderen
 * Seite, von der er zurueckfinden muss.
 *
 * Die Inhalte sind dieselben Bausteine wie auf /emails/:id (EmailBody,
 * AttachmentsSection, HistoryContextBadge). Bearbeiten — antworten,
 * weiterleiten, verschieben — bleibt bewusst der vollen Ansicht vorbehalten;
 * dafuer gibt es unten den Link. Ein Popup, das alles kann, ist keine
 * schnelle Ansicht mehr.
 */
export function EmailModal({
  emailId, open, onClose,
}: {
  emailId: number | null
  open: boolean
  onClose: () => void
}) {
  // Welche Mail des Verlaufs gerade gezeigt wird. Beim Oeffnen die
  // angeklickte; ein Klick in der Zeitleiste wechselt, ohne das Popup zu
  // schliessen.
  const [gezeigt, setGezeigt] = useState<number | null>(emailId)
  useEffect(() => { setGezeigt(emailId) }, [emailId])
  const aktuelleId = gezeigt ?? emailId

  // Keine eigene `queryFn` — siehe `lib/emailQuery.ts`.
  const { data, isLoading, error } = useQuery<EmailAntwort>({
    queryKey: ["email", String(aktuelleId)],
    enabled: open && !!aktuelleId,
  })

  // Der ganze Verlauf. Kommt aus der DB, nicht aus einer gefilterten Liste —
  // sonst fehlen Mails ausserhalb des Listenfensters (Bug 09.07.2026).
  const verlauf = useQuery({
    queryKey: ["email-thread", String(emailId)],
    enabled: open && !!emailId,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/emails/{email_id}/thread", {
        params: { path: { email_id: Number(emailId) } },
      })
      if (error) throw new Error(String(error))
      return data
    },
  })

  const e = data?.email
  const mails = verlauf.data?.items || []

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      breite={940}
      titel={e?.subject || (isLoading ? "Lädt…"
        : e?.inhalt_entfernt_am ? "Inhalt nach 90 Tagen entfernt" : "(ohne Betreff)")}
      untertitel={e && (
        <span style={{ display: "block", lineHeight: 1.6 }}>
          <strong style={{ color: "#475569" }}>Von:</strong> {e.sender}
          {e.recipient && <> · <strong style={{ color: "#475569" }}>An:</strong> {e.recipient}</>}
          {/* CC lag immer schon in der Antwort, wurde aber nirgends gezeigt.
              Diese Ansicht haengt an der Suche und am Dossier — dort faellt es
              genauso auf wie in der Detailseite. */}
          {e.cc && <> · <strong style={{ color: "#475569" }}>CC:</strong> {e.cc}</>}
          <br />
          {fmtRelative(e.received_at)}
          {e.imap_folder && <> · Ordner: {e.imap_folder}</>}
          {e.category && <> · {e.category}</>}
          {/* Bei gesendeten Mails: über das Portal oder im Mailprogramm? */}
          {e.sent_via && (
            <> · <span style={{
              fontWeight: 600,
              color: e.sent_via === "portal" ? "#4338ca" : "#64748b",
            }}>
              {e.sent_via === "portal"
                ? "über das Portal gesendet"
                : "im Mailprogramm gesendet"}
            </span></>
          )}
        </span>
      )}
      kopfRechts={e && (
        <>
          {/* #155: Mail-ID fuer Screenshot und „Problem melden". */}
          <MailIdMarke id={aktuelleId} />
          {aktuelleId && <HistoryContextBadge emailId={aktuelleId} />}
          <StatusBadge status={e.status} />
        </>
      )}
      fuss={emailId && (
        <>
          <Link
            to={`/emails/${aktuelleId}?von=suche&${new URLSearchParams(window.location.search)}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5,
              fontWeight: 600, color: "#0f172a", background: "#fff",
              border: "1px solid #cbd5e1", borderRadius: 8, padding: "6px 12px",
              textDecoration: "none",
            }}
          >
            <ExternalLink style={{ width: 13, height: 13 }} />
            Ganze Ansicht — antworten, weiterleiten, verschieben
          </Link>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>Esc schließt</span>
        </>
      )}
    >
      {isLoading && (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
          Lädt E-Mail…
        </div>
      )}
      {error && (
        <div style={{ padding: 24, color: "#991b1b", fontSize: 13 }}>
          E-Mail konnte nicht geladen werden: {String(error)}
        </div>
      )}
      {e && (
        <>
          {/* Der ganze Verlauf. Erst ab zwei Mails — bei einer einzelnen waere
              eine „Thread"-Leiste nur Rauschen. */}
          {mails.length > 1 && (
            <ThreadTimeline
              mails={mails}
              activeId={Number(aktuelleId)}
              onSelect={(id) => setGezeigt(id)}
            />
          )}

          {/* Als Aufforderung, nicht als Datenfeld (#7) — dieselbe Darstellung
              wie in der Detailseite, damit es ueberall gleich gelesen wird. */}
          {e.handlungsbedarf && (
            <div style={{
              margin: "14px 18px 0", padding: "10px 13px",
              background: "rgba(245, 158, 11, 0.10)",
              borderLeft: "3px solid #f59e0b", borderRadius: 6,
              fontSize: 12.5, color: "#334155", lineHeight: 1.5,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
                textTransform: "uppercase", color: "#b45309", marginBottom: 2,
              }}>
                Zu tun
              </div>
              {e.handlungsbedarf}
            </div>
          )}

          <div style={{ padding: "14px 18px" }}>
            {e.inhalt_entfernt_am ? (
              // Datenschutz-Löschjob: Inhalt nach 90 Tagen geleert.
              <InhaltEntferntHinweis
                tenant={e.tenant_slug}
                messageId={e.message_id}
                folder={e.imap_folder}
                postfach={e.postfach}
                postfachVerfuegbar={e.postfach_verfuegbar}
              />
            ) : (
              <EmailBody
                html={stripAttachmentDumps(e.body_html || e.body_text || "")}
                emailId={Number(aktuelleId)}
                minHeight={220}
              />
            )}
          </div>

          {/* Die Ueberschrift geht MIT hinein — sonst bleibt sie als leere
              Zeile stehen, wenn die Mail nur eingebettete Bilder traegt. */}
          {e.has_attachments && (
            <div style={{ padding: "0 18px 16px" }}>
              <AttachmentsSection
                emailId={Number(aktuelleId)}
                tenantSlug={e.tenant_slug || undefined}
                hatteAnhaenge
                titel={
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 12,
                    fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
                    color: "#94a3b8", marginBottom: 8,
                  }}>
                    <Paperclip style={{ width: 12, height: 12 }} /> Anhänge
                  </div>
                }
              />
            </div>
          )}

          {/* 🔴 Der Entwurf ist KEIN Teil des Verlaufs — er wurde nie
              gesendet. Als schlichter Textblock unter der Mail sah er aus wie
              die naechste Nachricht im Thread (Rueckfrage 13.08.2026). Darum
              jetzt abgesetzt, mit Stift-Symbol und ausdruecklichem Hinweis. */}
          {e.draft_response && (
            <div style={{ padding: "0 18px 18px" }}>
              <div style={{
                border: "1px dashed #a5b4fc", borderRadius: 10,
                background: "#eef2ff", overflow: "hidden",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "8px 13px", borderBottom: "1px dashed #c7d2fe",
                }}>
                  <PencilLine style={{ width: 13, height: 13, color: "#4f46e5" }} />
                  <span style={{
                    fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: 0.4, color: "#4338ca",
                  }}>
                    Vorgeschlagene Antwort
                  </span>
                  <span style={{ fontSize: 12, color: "#6366f1" }}>
                    — noch nicht gesendet, von der KI entworfen
                  </span>
                </div>
                <div style={{
                  fontSize: 13, color: "#312e81", padding: 13,
                  whiteSpace: "pre-wrap", lineHeight: 1.55,
                }}>
                  {e.draft_response}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </ModalShell>
  )
}
